/**
 * deviceMove -- wording for moving a device to another site and for what the move changed
 * (OP#1935, 2026-10-19).
 *
 * WHY: the device-page Move and the Devices bulk "Assign" call POST /devices/assign, the same
 * endpoint as first claim. Until this fix the server silently re-applied the target customer's
 * onboarding default to an ALREADY-PLACED device -- rename, tags and a queued firmware change
 * (which could be a DOWNGRADE) -- and the UI only said "Moved.". Now:
 *   * a move changes nothing but the placement unless the operator ticks an UNCHECKED
 *     "Also apply the customer's onboarding default" box, which lists what it would change
 *     (customerDefaultChanges);
 *   * the result line reports what was ACTUALLY applied, from the server's per-device `results`
 *     and its `firmware_queued` count (moveResultLines) -- never a bare "Moved.".
 *
 * Pure (no React, no fetch): unit-tested in deviceMove.test.ts (`npm run test:unit`).
 */

/** The customer's persisted onboarding default (GET /orgs/<id>/onboarding-profile). */
export interface OnboardingDefault {
  naming_pattern: string | null;
  tags: string[];
  firmware_policy: string;
}

/** One assigned device in the POST /devices/assign response (api/v2/devices.py). */
export interface AssignResultItem {
  serial: string;
  /** "claim" = first claim (no site / quarantine pool); "move" = already placed elsewhere. */
  mode: "claim" | "move";
  /** True when the onboarding template was applied to this device. */
  customer_default: boolean;
  renamed: { from: string | null; to: string } | null;
  tags: string[] | null;
  /** Firmware decision code: queued | held_vendor | not_newer | unknown_version | no_image | off. */
  firmware: string | null;
  /** Operator-facing firmware sentence, set for every decision worth saying out loud. */
  firmware_note: string | null;
}

/** POST /devices/assign response. `results` is absent on controllers older than OP#1935. */
export interface AssignResponse {
  assigned: number;
  skipped: number;
  site_id: number;
  firmware_queued?: number;
  results?: AssignResultItem[];
}

/** The body flag that opts a MOVE in to the customer default (server: `is True` only). */
export const APPLY_CUSTOMER_DEFAULT = "apply_customer_default";

/** Shown on a move when the operator must ask for firmware themselves (CyberTan / Edgecore). */
export const FIRMWARE_EXPLICIT_NOTE = "Firmware upgrade must be requested explicitly.";

/**
 * What ticking "Also apply the customer's onboarding default" would change, one line each.
 * Empty when the customer has no default worth applying (the UI then hides the box).
 */
export function customerDefaultChanges(p: OnboardingDefault | null | undefined): string[] {
  if (!p) return [];
  const out: string[] = [];
  const pattern = (p.naming_pattern ?? "").trim();
  if (pattern) out.push(`Rename the device using the pattern "${pattern}"`);
  const tags = (p.tags ?? []).filter((t) => t && t.trim());
  if (tags.length) out.push(`Set group tags: ${tags.join(", ")}`);
  if (p.firmware_policy && p.firmware_policy !== "off") {
    out.push(
      `Queue a firmware upgrade when a newer approved image exists (policy: ${p.firmware_policy}). ` +
        "Never a downgrade; CyberTan/SonicFi and Edgecore units are never queued on a move.",
    );
  }
  return out;
}

/**
 * The result lines after a move/assign: the placement, then exactly what else was applied.
 * Reads `firmware_queued` (the count of queued upgrades) and the per-device `results`.
 */
export function moveResultLines(r: AssignResponse, siteName: string): string[] {
  if (r.assigned === 0) {
    return [`Not moved: the controller skipped the device (${r.skipped} skipped).`];
  }
  const where = siteName ? ` to ${siteName}` : "";
  const head =
    r.assigned === 1 ? `Moved${where}.` : `Moved ${r.assigned} devices${where}.`;
  const lines = [r.skipped ? `${head} Skipped ${r.skipped}.` : head];
  const results = r.results ?? [];
  for (const it of results) {
    if (it.renamed) lines.push(`${it.serial}: renamed to ${it.renamed.to}.`);
    if (it.tags && it.tags.length) lines.push(`${it.serial}: tags set to ${it.tags.join(", ")}.`);
    // A queued upgrade is reported once below from firmware_queued; every other decision that
    // needs saying (held for CyberTan/Edgecore, not newer, version unknown) is listed here.
    if (it.firmware && it.firmware !== "queued" && it.firmware_note) {
      lines.push(`${it.serial}: ${it.firmware_note}`);
    }
  }
  const fwQueued = r.firmware_queued ?? 0;
  if (fwQueued > 0) {
    lines.push(
      fwQueued === 1
        ? "A firmware upgrade was queued."
        : `Firmware upgrades were queued for ${fwQueued} devices.`,
    );
  }
  const anythingElse =
    fwQueued > 0 || results.some((it) => it.renamed || (it.tags && it.tags.length) || it.firmware_note);
  if (results.length > 0 && !anythingElse) {
    lines.push("Nothing else changed: name, tags and firmware are untouched.");
  }
  return lines;
}

